import {
  useImperativeHandle,
  type PropsWithChildren,
  type Ref,
  type RefObject,
} from 'react';

import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { INTERACTION_MODE } from '../../core/components/interactions-overlay/types.js';
import { useTriggerZoom } from '../../core/components/zoom/hooks/useTriggerZoom.js';
import { ChartType } from '../../core/types/chart-types.js';
import { ZOOM_CHANGE_TYPE } from '../../core/types/zoom.js';
import { useApplyZoomDelta } from '../components/overlay/hooks/useApplyZoomDelta.js';
import { useResetInteractions } from '../components/overlay/hooks/useResetInteractions.js';
import { useDownload } from '../hooks/useDownload.js';
import { useXYChartData } from '../hooks/useXYChartData.js';
import { reduceInteractionMode } from '../store/reduce-interaction-mode.js';
import { useSetState, useZoomState } from '../store/selectors.js';
import type { XYChartRef } from '../types/xy-chart.js';
import { buildAndDownloadCsv } from '../utils/build-and-download-csv.js';
import type { XYChartConfig } from '../utils/xy-chart-configuration.js';

interface XYChartImperativeHandlerBuilderProps {
  chartRef: Ref<XYChartRef>;
  containerRef: RefObject<HTMLDivElement | null>;
  config: XYChartConfig;
}

export const XYChartImperativeHandlerBuilder = ({
  chartRef,
  containerRef,
  config,
  children,
}: PropsWithChildren<XYChartImperativeHandlerBuilderProps>) => {
  const { seriesWithDatapoints, datapointMetadataMap, seriesToMetadata } =
    useXYChartData();
  const { downloadDataCallback } = useDownload();
  const setState = useSetState();
  const { zoomInEnabled, zoomOutEnabled } = useZoomState();
  const { axesMetadata } = useXYChartData();

  const { width } = useGridPlotSize();
  const applyZoom = useApplyZoomDelta();
  const triggerZoom = useTriggerZoom(width, applyZoom);

  const resetInteractions = useResetInteractions();

  useImperativeHandle(chartRef, () => ({
    element: containerRef.current,
    getConfig: () => {
      const { tooltip, ...exportableConfig } = config;
      return JSON.stringify({
        type: ChartType.XYChart,
        ...exportableConfig,
        tooltip: { hidden: !!tooltip?.hidden },
      });
    },
    downloadData: () => {
      buildAndDownloadCsv(
        seriesWithDatapoints,
        seriesToMetadata,
        datapointMetadataMap,
        downloadDataCallback,
      );
    },
    exploreMode: () => {
      setState(reduceInteractionMode(INTERACTION_MODE.Explore));
    },
    zoomMode: () => {
      if (axesMetadata.isZoomEnabled) {
        setState(reduceInteractionMode(INTERACTION_MODE.Zoom));
      }
    },
    panMode: () => {
      if (zoomOutEnabled && axesMetadata.isPanEnabled) {
        setState(reduceInteractionMode(INTERACTION_MODE.Pan));
      }
    },
    zoomIn: () => {
      if (zoomInEnabled) {
        triggerZoom(ZOOM_CHANGE_TYPE.ZoomIn);
      }
    },
    zoomOut: () => {
      if (zoomOutEnabled) {
        triggerZoom(ZOOM_CHANGE_TYPE.ZoomOut);
      }
    },
    reset: () => {
      resetInteractions();
    },
  }));

  return children;
};
